
import React from 'react';
import { Language, AgentProfile } from '../../types';
import RobotEmptyState from './RobotEmptyState';
import RobotDeleteModal from './RobotDeleteModal';
import RobotHeader from './RobotHeader';
import RobotList from './RobotList';
import RobotVisual from './RobotVisual';
import RobotOptions from './RobotOptions';

interface RobotComponentProps {
  language: Language;
  activeAgent: AgentProfile | undefined;
  agents: AgentProfile[];
  isActive: boolean;
  state: 'idle' | 'listening' | 'processing' | 'speaking';
  activeTab: 'agents' | 'visual' | 'options';
  agentLimit: number;
  agentToDelete: string | null;
  hasApiAccess: boolean;
  
  onBack: () => void;
  onSetActiveAgent: (id: string) => void;
  onSetActiveTab: (tab: 'agents' | 'visual' | 'options') => void;
  onToggleRobot: () => void;
  onAddAgent: () => void;
  onDeleteAgent: (id: string) => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  onDuplicateAgent: (agent: AgentProfile) => void;
  onResetAgent: (id: string) => void;
  onUpdateAgent: (updates: Partial<AgentProfile>) => void;
  onCopyApiKey: () => void;
}

const RobotComponent: React.FC<RobotComponentProps> = (props) => {
  const { 
    language, activeAgent, agents, isActive, state, activeTab, agentLimit, agentToDelete,
    hasApiAccess, onBack, onSetActiveAgent, onSetActiveTab, onToggleRobot, onAddAgent,
    onDeleteAgent, onConfirmDelete, onCancelDelete, onDuplicateAgent, onResetAgent,
    onUpdateAgent, onCopyApiKey
  } = props;

  const isRtl = language === 'ar';

  if (!activeAgent) {
    return <RobotEmptyState language={language} onBack={onBack} onAddAgent={onAddAgent} isRtl={isRtl} />;
  }

  return (
    <div className="flex h-full w-full bg-slate-50 dark:bg-[#050507] text-slate-800 dark:text-slate-200 overflow-hidden relative font-sans transition-colors duration-500">
      {/* Background Ambience */}
      <div className="absolute inset-0 pointer-events-none">
         <div className={`absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(16,185,129,0.02),transparent_70%)] ${state === 'speaking' ? 'animate-pulse' : ''}`}></div>
      </div>

      {agentToDelete && (
        <RobotDeleteModal language={language} onCancel={onCancelDelete} onConfirm={onConfirmDelete} isRtl={isRtl} />
      )}

      <RobotHeader 
        language={language}
        activeAgent={activeAgent}
        isActive={isActive}
        hasApiAccess={hasApiAccess}
        activeTab={activeTab}
        onBack={onBack}
        onSetActiveTab={onSetActiveTab}
        isRtl={isRtl}
      />

      <div className="flex-1 flex flex-col lg:flex-row pt-16 lg:pt-20 h-full relative">
        <RobotList 
          language={language}
          activeAgent={activeAgent}
          agents={agents}
          agentLimit={agentLimit}
          activeTab={activeTab}
          onAddAgent={onAddAgent}
          onSetActiveAgent={onSetActiveAgent}
          onSetActiveTab={onSetActiveTab}
          onDuplicateAgent={onDuplicateAgent}
          onResetAgent={onResetAgent}
          onDeleteAgent={onDeleteAgent}
          isRtl={isRtl}
        />

        <RobotVisual 
          activeAgent={activeAgent}
          isActive={isActive}
          state={state}
          activeTab={activeTab}
          onToggleRobot={onToggleRobot}
        />

        <RobotOptions 
          language={language}
          activeAgent={activeAgent}
          activeTab={activeTab}
          hasApiAccess={hasApiAccess}
          onUpdateAgent={onUpdateAgent}
          onCopyApiKey={onCopyApiKey}
          isRtl={isRtl}
        />
      </div>
    </div>
  );
};

export default RobotComponent;
